import React, { useState, useEffect } from 'react';
import { X, Copy, Check, ChevronDown, ChevronUp, Package, Users, Image, DollarSign, Target, Lightbulb, MessageSquare, Award } from 'lucide-react';
import knowledgeApiService from '../../services/api/knowledge/knowledgeApiService';
import { Product, Competitor, ProductImage, TrustProof } from '../../types/knowledge';

interface ProductInfoCopyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCopyToPrompt?: (text: string) => void;
  /** 商品選択時に商品データとテキストの両方を受け取るコールバック（product_data送信用） */
  onSelectProduct?: (product: Product, text: string) => void;
}

const ProductInfoCopyModal: React.FC<ProductInfoCopyModalProps> = ({
  isOpen,
  onClose,
  onCopyToPrompt,
  onSelectProduct,
}) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    basic: true,
    pricingOffer: true,
    target: true,
    benefits: true,
    positioning: true,
    appeal: true,
    competitors: false,
    images: false,
  });
  const [copiedField, setCopiedField] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      fetchProducts();
    } else {
      setSelectedProduct(null);
      setError(null);
    }
  }, [isOpen]);

  const fetchProducts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await knowledgeApiService.getProducts({ status: 'active' });
      setProducts(data);
    } catch (err: any) {
      console.error('Failed to fetch products:', err);
      setError(err?.message || '商品の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopyToPrompt = (text: string) => {
    // onSelectProductがある場合は商品データとテキストの両方を渡す
    if (onSelectProduct && selectedProduct) {
      onSelectProduct(selectedProduct, text);
      onClose();
    } else if (onCopyToPrompt) {
      onCopyToPrompt(text);
      onClose();
    }
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const getCategoryLabel = (category: string): string => {
    const labels: Record<string, string> = {
      brand_logo: 'ブランドロゴ',
      product_image: '商品/サービス画像',
      material: 'イメージ素材',
    };
    return labels[category] || category;
  };

  /**
   * 商品情報を画像生成プロンプト用の文章形式に整形する
   * バックエンドのImagePromptGeneratorServiceと連携して使用
   */
  const formatProductInfoForPrompt = (product: Product): string => {
    const meta = product.extended_metadata;
    const productInfo = meta?.product;
    const pricingOffer = meta?.pricing_offer;
    const target = meta?.target;
    const benefits = meta?.benefits;
    const positioning = meta?.positioning;
    const appeal = meta?.appeal;
    const segments = meta?.segments;

    let text = '';

    // 商品の基本説明を文章形式で記述
    text += `「${product.name}」`;
    if (productInfo?.brand) {
      text += `（${productInfo.brand}）`;
    }
    text += 'の広告を作成します。\n\n';

    // カテゴリと説明
    if (productInfo?.category || product.category) {
      text += `${productInfo?.category || product.category}カテゴリの商品で、`;
    }
    if (productInfo?.description || product.description) {
      const desc = productInfo?.description || product.description || '';
      text += desc.endsWith('。') ? desc : desc + '。';
      text += '\n';
    }

    // キャッチコピー（あれば強調）
    if (productInfo?.tagline) {
      text += `\nキャッチコピー: 「${productInfo.tagline}」\n`;
    }

    // CTA・価格表示設定（pricing_offerから取得）
    if (pricingOffer?.main_cta || pricingOffer?.price_visibility) {
      text += `\n【CTA・価格表示設定】\n`;
      if (pricingOffer.main_cta) {
        text += `メインCTA: ${pricingOffer.main_cta}\n`;
      }
      if (pricingOffer.price_visibility) {
        text += `価格表示設定: ${pricingOffer.price_visibility}\n`;
      }
    }

    // ターゲット情報
    if (target?.segment) {
      text += `\n【ターゲット】\n`;
      text += `${target.segment}\n`;
      if (target.insights?.pain_emotion) {
        text += `・悩み: ${target.insights.pain_emotion}\n`;
      }
      if (target.insights?.ideal_state) {
        text += `・理想状態: ${target.insights.ideal_state}\n`;
      }
    }

    // ベネフィット
    if (benefits?.functional || benefits?.emotional) {
      text += `\n【ベネフィット】\n`;
      if (benefits.functional) {
        text += `・機能的: ${benefits.functional}\n`;
      }
      if (benefits.emotional) {
        text += `・感情的: ${benefits.emotional}\n`;
      }
    }

    // USP・差別化ポイント
    if (productInfo?.usp) {
      text += `\n【USP（独自の強み）】\n${productInfo.usp}\n`;
    }
    if (positioning?.pod && positioning.pod.length > 0) {
      text += `\n【差別化ポイント】\n`;
      positioning.pod.forEach(item => {
        text += `・${item}\n`;
      });
    }

    // 訴求情報
    if (appeal?.direction) {
      text += `\n【訴求の方向性】\n${appeal.direction}\n`;
    }
    if (appeal?.winning_copy) {
      text += `\n【参考コピー】\n${appeal.winning_copy}\n`;
    }

    // 複数セグメント情報（詳細）
    if (segments && segments.length > 0) {
      text += `\n【ターゲットセグメント詳細】\n`;
      segments.forEach((segment, index) => {
        text += `\n■ セグメント${index + 1}: ${segment.name}\n`;
        if (segment.funnel_stage) {
          text += `  ファネル層: ${segment.funnel_stage}\n`;
        }
        // デモグラフィック情報
        const demographics = [];
        if (segment.gender) demographics.push(segment.gender);
        if (segment.age_range) demographics.push(segment.age_range);
        if (segment.occupation) demographics.push(segment.occupation);
        if (demographics.length > 0) {
          text += `  属性: ${demographics.join(' / ')}\n`;
        }
        // インサイト
        if (segment.insights) {
          if (segment.insights.pain_emotion) {
            text += `  課題・悩み: ${segment.insights.pain_emotion}\n`;
          }
          if (segment.insights.ideal_state) {
            text += `  理想状態: ${segment.insights.ideal_state}\n`;
          }
          if (segment.insights.barriers) {
            text += `  障壁: ${segment.insights.barriers}\n`;
          }
          if (segment.insights.purchase_factor) {
            text += `  購買決定要因: ${segment.insights.purchase_factor}\n`;
          }
        }
        // ベネフィット
        if (segment.benefits) {
          if (segment.benefits.functional) {
            text += `  機能的ベネフィット: ${segment.benefits.functional}\n`;
          }
          if (segment.benefits.emotional) {
            text += `  感情的ベネフィット: ${segment.benefits.emotional}\n`;
          }
          if (segment.benefits.self_realization) {
            text += `  自己実現ベネフィット: ${segment.benefits.self_realization}\n`;
          }
        }
        // 訴求軸
        if (segment.appeals && segment.appeals.length > 0) {
          text += `  訴求:\n`;
          segment.appeals.forEach((appealItem, appealIndex) => {
            text += `    ${appealIndex + 1}. ${appealItem.name}`;
            if (appealItem.direction) {
              text += ` - ${appealItem.direction}`;
            }
            text += '\n';
            if (appealItem.copy_example) {
              text += `       例文: ${appealItem.copy_example}\n`;
            }
          });
        }
      });
    }

    // 価格・オファー情報
    if (pricingOffer?.price_details || pricingOffer?.offers_campaigns) {
      text += `\n【価格・オファー】\n`;
      if (pricingOffer.price_details) {
        text += `${pricingOffer.price_details}\n`;
      }
      if (pricingOffer.offers_campaigns) {
        text += `キャンペーン: ${pricingOffer.offers_campaigns}\n`;
      }
    }

    return text.trim();
  };

  const formatProductInfo = (product: Product): string => {
    const meta = product.extended_metadata;
    const productInfo = meta?.product;
    const pricingOffer = meta?.pricing_offer;
    const target = meta?.target;
    const benefits = meta?.benefits;
    const positioning = meta?.positioning;
    const appeal = meta?.appeal;

    let info = `■ 基本情報\n`;
    info += `【商品名】${product.name}\n`;
    if (productInfo?.brand) {
      info += `【ブランド】${productInfo.brand}\n`;
    }
    if (productInfo?.category || product.category) {
      info += `【カテゴリ】${productInfo?.category || product.category}\n`;
    }
    if (productInfo?.market_type) {
      info += `【市場タイプ】${productInfo.market_type}\n`;
    }
    if (productInfo?.sales_model) {
      info += `【販売モデル】${productInfo.sales_model}\n`;
    }
    if (productInfo?.tagline) {
      info += `【キャッチコピー】${productInfo.tagline}\n`;
    }
    if (productInfo?.description || product.description) {
      info += `【説明】${productInfo?.description || product.description}\n`;
    }

    // 料金・オファー情報
    if (pricingOffer && (pricingOffer.main_cta || pricingOffer.price_visibility || pricingOffer.price_details || pricingOffer.offers_campaigns)) {
      info += `\n■ 料金・オファー情報\n`;
      if (pricingOffer.main_cta) {
        info += `【メインCTA】${pricingOffer.main_cta}\n`;
      }
      if (pricingOffer.price_visibility) {
        info += `【価格表示】${pricingOffer.price_visibility}\n`;
      }
      if (pricingOffer.price_details) {
        info += `【価格詳細】${pricingOffer.price_details}\n`;
      }
      if (pricingOffer.offers_campaigns) {
        info += `【オファー・キャンペーン】${pricingOffer.offers_campaigns}\n`;
      }
    }

    // ターゲット情報
    if (target && (target.segment || target.insights)) {
      info += `\n■ ターゲット情報\n`;
      if (target.segment) {
        info += `【セグメント】${target.segment}\n`;
      }
      if (target.insights?.pain_emotion) {
        info += `【課題・悩み】${target.insights.pain_emotion}\n`;
      }
      if (target.insights?.ideal_state) {
        info += `【理想状態】${target.insights.ideal_state}\n`;
      }
      if (target.insights?.barriers) {
        info += `【障壁】${target.insights.barriers}\n`;
      }
      if (target.insights?.purchase_factor) {
        info += `【購入要因】${target.insights.purchase_factor}\n`;
      }
    }

    // ベネフィット
    if (benefits && (benefits.functional || benefits.emotional || benefits.self_realization)) {
      info += `\n■ ベネフィット\n`;
      if (benefits.functional) {
        info += `【機能的ベネフィット】${benefits.functional}\n`;
      }
      if (benefits.emotional) {
        info += `【感情的ベネフィット】${benefits.emotional}\n`;
      }
      if (benefits.self_realization) {
        info += `【自己実現ベネフィット】${benefits.self_realization}\n`;
      }
    }

    // USP・ポジショニング
    if ((productInfo?.usp || positioning) && (productInfo?.usp || positioning?.pod?.length || positioning?.pop?.length || positioning?.trust_proof?.length)) {
      info += `\n■ USP・ポジショニング\n`;
      if (productInfo?.usp) {
        info += `【USP (Unique Selling Proposition)】${productInfo.usp}\n`;
      }
      if (positioning?.pod && positioning.pod.length > 0) {
        info += `【差別化ポイント (POD)】\n`;
        positioning.pod.forEach(item => {
          info += `・${item}\n`;
        });
      }
      if (positioning?.pop && positioning.pop.length > 0) {
        info += `【共通ポイント (POP)】\n`;
        positioning.pop.forEach(item => {
          info += `・${item}\n`;
        });
      }
      if (positioning?.trust_proof && positioning.trust_proof.length > 0) {
        info += `【引用元 (${positioning.trust_proof.length}件)】\n`;
        positioning.trust_proof.forEach((source) => {
          if (typeof source === 'string') {
            info += `・${source}\n`;
          } else {
            const displayStatement = source.statement || '';
            const displayTitle = source.title || source.source || '';
            const displayUrl = source.url || '';
            if (displayStatement) {
              info += `・${displayStatement}\n`;
            }
            if (displayTitle) {
              info += `  ${displayTitle}${displayUrl ? ` (${displayUrl})` : ''}\n`;
            }
          }
        });
      }
    }

    // 訴求情報
    if (appeal && (appeal.recommended_types?.length || appeal.direction || appeal.winning_copy || appeal.structure_pattern)) {
      info += `\n■ 訴求情報\n`;
      if (appeal.recommended_types && appeal.recommended_types.length > 0) {
        info += `【推奨訴求タイプ】${appeal.recommended_types.join('、')}\n`;
      }
      if (appeal.direction) {
        info += `【訴求方向性】${appeal.direction}\n`;
      }
      if (appeal.winning_copy) {
        info += `【勝ちコピー】${appeal.winning_copy}\n`;
      }
      if (appeal.structure_pattern) {
        info += `【構成パターン】${appeal.structure_pattern}\n`;
      }
    }

    // URL情報
    if (product.url) {
      info += `\n■ URL情報\n`;
      info += `【登録日時】${new Date(product.created_at).toLocaleString('ja-JP')}\n`;
      const statusLabel = typeof product.status === 'number'
        ? (product.status === 1 ? '稼働中' : '非稼働')
        : (product.status === 'active' ? '稼働中' : '非稼働');
      info += `【ステータス】${statusLabel}\n`;
    }

    return info.trim();
  };

  const formatCompetitorInfo = (competitor: Competitor): string => {
    let info = `【競合名】${competitor.name}\n`;
    if (competitor.company_url) {
      info += `【URL】${competitor.company_url}\n`;
    }
    if (competitor.description) {
      info += `【概要】${competitor.description}\n`;
    }
    if (competitor.strengths) {
      info += `【強み】${competitor.strengths}\n`;
    }
    if (competitor.weaknesses) {
      info += `【弱み】${competitor.weaknesses}\n`;
    }
    return info.trim();
  };

  const formatImageInfo = (image: ProductImage): string => {
    let info = `【カテゴリ】${getCategoryLabel(image.category)}\n`;
    if (image.title) {
      info += `【タイトル】${image.title}\n`;
    }
    if (image.description) {
      info += `【説明】${image.description}\n`;
    }
    return info.trim();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleOverlayClick}
    >
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">商品情報をコピー</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1"
            aria-label="閉じる"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden flex">
          {/* Product List (Left Panel) */}
          <div className="w-1/3 border-r border-gray-200 overflow-y-auto">
            <div className="p-4">
              <h3 className="text-sm font-medium text-gray-700 mb-3">商品一覧</h3>
              {loading ? (
                <div className="flex flex-col items-center justify-center py-8">
                  <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <p className="mt-3 text-sm text-gray-600">読み込み中...</p>
                </div>
              ) : error ? (
                <div className="text-center py-8">
                  <p className="text-red-600 text-sm">{error}</p>
                  <button
                    onClick={fetchProducts}
                    className="mt-3 px-3 py-1.5 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                  >
                    再試行
                  </button>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-600 text-sm">商品が登録されていません</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {products.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => setSelectedProduct(product)}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        selectedProduct?.id === product.id
                          ? 'border-blue-500 bg-blue-50'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-gray-900 text-sm truncate">
                        {product.name}
                      </div>
                      {product.category && (
                        <div className="text-xs text-gray-500 mt-1">{product.category}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Product Detail (Right Panel) */}
          <div className="flex-1 overflow-y-auto">
            {selectedProduct ? (
              <div className="p-6">
                {/* Copy All Button */}
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">{selectedProduct.name}</h3>
                  <div className="flex gap-2">
                    <button
                      onClick={() => copyToClipboard(formatProductInfo(selectedProduct), 'all')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                    >
                      {copiedField === 'all' ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">コピー済み</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>すべてコピー</span>
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => copyToClipboard(formatProductInfoForPrompt(selectedProduct), 'prompt_format')}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-100 hover:bg-green-200 text-green-700 rounded-md transition-colors"
                    >
                      {copiedField === 'prompt_format' ? (
                        <>
                          <Check className="w-4 h-4 text-green-600" />
                          <span className="text-green-600">コピー済み</span>
                        </>
                      ) : (
                        <>
                          <Copy className="w-4 h-4" />
                          <span>プロンプト形式</span>
                        </>
                      )}
                    </button>
                    {(onCopyToPrompt || onSelectProduct) && (
                      <button
                        onClick={() => handleCopyToPrompt(formatProductInfoForPrompt(selectedProduct))}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-md transition-colors"
                      >
                        プロンプトに追加
                      </button>
                    )}
                  </div>
                </div>

                {/* Basic Info Section */}
                <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleSection('basic')}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <Package className="w-4 h-4 text-blue-600" />
                      <span className="font-medium text-gray-700">基本情報</span>
                    </div>
                    {expandedSections.basic ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </button>
                  {expandedSections.basic && (
                    <div className="p-4 space-y-3">
                      <CopyableField
                        label="商品名"
                        value={selectedProduct.name}
                        fieldName="name"
                        copiedField={copiedField}
                        onCopy={copyToClipboard}
                      />
                      {selectedProduct.extended_metadata?.product?.brand && (
                        <CopyableField
                          label="ブランド"
                          value={selectedProduct.extended_metadata.product.brand}
                          fieldName="brand"
                          copiedField={copiedField}
                          onCopy={copyToClipboard}
                        />
                      )}
                      {(selectedProduct.extended_metadata?.product?.category || selectedProduct.category) && (
                        <CopyableField
                          label="カテゴリ"
                          value={selectedProduct.extended_metadata?.product?.category || selectedProduct.category || ''}
                          fieldName="category"
                          copiedField={copiedField}
                          onCopy={copyToClipboard}
                        />
                      )}
                      {selectedProduct.extended_metadata?.product?.market_type && (
                        <CopyableField
                          label="市場タイプ"
                          value={selectedProduct.extended_metadata.product.market_type}
                          fieldName="market_type"
                          copiedField={copiedField}
                          onCopy={copyToClipboard}
                        />
                      )}
                      {selectedProduct.extended_metadata?.product?.sales_model && (
                        <CopyableField
                          label="販売モデル"
                          value={selectedProduct.extended_metadata.product.sales_model}
                          fieldName="sales_model"
                          copiedField={copiedField}
                          onCopy={copyToClipboard}
                        />
                      )}
                      {selectedProduct.extended_metadata?.product?.tagline && (
                        <CopyableField
                          label="キャッチコピー"
                          value={selectedProduct.extended_metadata.product.tagline}
                          fieldName="tagline"
                          copiedField={copiedField}
                          onCopy={copyToClipboard}
                        />
                      )}
                      {(selectedProduct.extended_metadata?.product?.description || selectedProduct.description) && (
                        <CopyableField
                          label="説明"
                          value={selectedProduct.extended_metadata?.product?.description || selectedProduct.description || ''}
                          fieldName="description"
                          copiedField={copiedField}
                          onCopy={copyToClipboard}
                          multiline
                        />
                      )}
                    </div>
                  )}
                </div>

                {/* Pricing & Offer Section */}
                {selectedProduct.extended_metadata?.pricing_offer && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('pricingOffer')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-gray-700">料金・オファー情報</span>
                      </div>
                      {expandedSections.pricingOffer ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.pricingOffer && (
                      <div className="p-4 space-y-3">
                        {selectedProduct.extended_metadata.pricing_offer.main_cta && (
                          <CopyableField
                            label="メインCTA"
                            value={selectedProduct.extended_metadata.pricing_offer.main_cta}
                            fieldName="main_cta"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        )}
                        {selectedProduct.extended_metadata.pricing_offer.price_visibility && (
                          <CopyableField
                            label="価格表示"
                            value={selectedProduct.extended_metadata.pricing_offer.price_visibility}
                            fieldName="price_visibility"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        )}
                        {selectedProduct.extended_metadata.pricing_offer.price_details && (
                          <CopyableField
                            label="価格詳細"
                            value={selectedProduct.extended_metadata.pricing_offer.price_details}
                            fieldName="price_details"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.pricing_offer.offers_campaigns && (
                          <CopyableField
                            label="オファー・キャンペーン"
                            value={selectedProduct.extended_metadata.pricing_offer.offers_campaigns}
                            fieldName="offers_campaigns"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Target Section */}
                {selectedProduct.extended_metadata?.target && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('target')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Target className="w-4 h-4 text-blue-600" />
                        <span className="font-medium text-gray-700">ターゲット情報</span>
                      </div>
                      {expandedSections.target ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.target && (
                      <div className="p-4 space-y-3">
                        {selectedProduct.extended_metadata.target.segment && (
                          <CopyableField
                            label="セグメント"
                            value={selectedProduct.extended_metadata.target.segment}
                            fieldName="segment"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.target.insights?.pain_emotion && (
                          <CopyableField
                            label="課題・悩み"
                            value={selectedProduct.extended_metadata.target.insights.pain_emotion}
                            fieldName="pain_emotion"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.target.insights?.ideal_state && (
                          <CopyableField
                            label="理想状態"
                            value={selectedProduct.extended_metadata.target.insights.ideal_state}
                            fieldName="ideal_state"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.target.insights?.barriers && (
                          <CopyableField
                            label="障壁"
                            value={selectedProduct.extended_metadata.target.insights.barriers}
                            fieldName="barriers"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.target.insights?.purchase_factor && (
                          <CopyableField
                            label="購入要因"
                            value={selectedProduct.extended_metadata.target.insights.purchase_factor}
                            fieldName="purchase_factor"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Benefits Section */}
                {selectedProduct.extended_metadata?.benefits && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('benefits')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Award className="w-4 h-4 text-green-600" />
                        <span className="font-medium text-gray-700">ベネフィット</span>
                      </div>
                      {expandedSections.benefits ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.benefits && (
                      <div className="p-4 space-y-3">
                        {selectedProduct.extended_metadata.benefits.functional && (
                          <CopyableField
                            label="機能的ベネフィット"
                            value={selectedProduct.extended_metadata.benefits.functional}
                            fieldName="functional"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.benefits.emotional && (
                          <CopyableField
                            label="感情的ベネフィット"
                            value={selectedProduct.extended_metadata.benefits.emotional}
                            fieldName="emotional"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.benefits.self_realization && (
                          <CopyableField
                            label="自己実現ベネフィット"
                            value={selectedProduct.extended_metadata.benefits.self_realization}
                            fieldName="self_realization"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* USP & Positioning Section */}
                {(selectedProduct.extended_metadata?.product?.usp || selectedProduct.extended_metadata?.positioning) && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('positioning')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Lightbulb className="w-4 h-4 text-yellow-600" />
                        <span className="font-medium text-gray-700">USP・ポジショニング</span>
                      </div>
                      {expandedSections.positioning ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.positioning && (
                      <div className="p-4 space-y-3">
                        {selectedProduct.extended_metadata?.product?.usp && (
                          <CopyableField
                            label="USP (Unique Selling Proposition)"
                            value={selectedProduct.extended_metadata.product.usp}
                            fieldName="usp"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata?.positioning?.pod && selectedProduct.extended_metadata.positioning.pod.length > 0 && (
                          <div className="group">
                            <label className="text-xs font-medium text-gray-500 mb-1 block">差別化ポイント (POD)</label>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                              {selectedProduct.extended_metadata.positioning.pod.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                            <button
                              onClick={() => copyToClipboard(selectedProduct.extended_metadata?.positioning?.pod?.join('\n') || '', 'pod')}
                              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                            >
                              {copiedField === 'pod' ? 'コピー済み ✓' : 'コピー'}
                            </button>
                          </div>
                        )}
                        {selectedProduct.extended_metadata?.positioning?.pop && selectedProduct.extended_metadata.positioning.pop.length > 0 && (
                          <div className="group">
                            <label className="text-xs font-medium text-gray-500 mb-1 block">共通ポイント (POP)</label>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-900">
                              {selectedProduct.extended_metadata.positioning.pop.map((item, idx) => (
                                <li key={idx}>{item}</li>
                              ))}
                            </ul>
                            <button
                              onClick={() => copyToClipboard(selectedProduct.extended_metadata?.positioning?.pop?.join('\n') || '', 'pop')}
                              className="mt-2 text-xs text-blue-600 hover:text-blue-700"
                            >
                              {copiedField === 'pop' ? 'コピー済み ✓' : 'コピー'}
                            </button>
                          </div>
                        )}
                        {selectedProduct.extended_metadata?.positioning?.trust_proof && selectedProduct.extended_metadata.positioning.trust_proof.length > 0 && (
                          <div className="group">
                            <label className="text-xs font-medium text-gray-500 mb-1 block">引用元 ({selectedProduct.extended_metadata.positioning.trust_proof.length}件)</label>
                            <div className="space-y-2">
                              {selectedProduct.extended_metadata.positioning.trust_proof.map((source, idx) => (
                                <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                                  {typeof source === 'string' ? (
                                    <p className="text-gray-700">{source}</p>
                                  ) : (
                                    <>
                                      {source.statement && <p className="text-gray-600 mb-1">{source.statement}</p>}
                                      {source.url ? (
                                        <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">
                                          {source.title || source.source || source.url}
                                        </a>
                                      ) : (source.title || source.source) && (
                                        <p className="text-gray-700 text-xs">{source.title || source.source}</p>
                                      )}
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Appeal Section */}
                {selectedProduct.extended_metadata?.appeal && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('appeal')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4 text-purple-600" />
                        <span className="font-medium text-gray-700">訴求情報</span>
                      </div>
                      {expandedSections.appeal ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.appeal && (
                      <div className="p-4 space-y-3">
                        {selectedProduct.extended_metadata.appeal.recommended_types && selectedProduct.extended_metadata.appeal.recommended_types.length > 0 && (
                          <CopyableField
                            label="推奨訴求タイプ"
                            value={selectedProduct.extended_metadata.appeal.recommended_types.join('、')}
                            fieldName="recommended_types"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        )}
                        {selectedProduct.extended_metadata.appeal.direction && (
                          <CopyableField
                            label="訴求方向性"
                            value={selectedProduct.extended_metadata.appeal.direction}
                            fieldName="direction"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.appeal.winning_copy && (
                          <CopyableField
                            label="勝ちコピー"
                            value={selectedProduct.extended_metadata.appeal.winning_copy}
                            fieldName="winning_copy"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                            multiline
                          />
                        )}
                        {selectedProduct.extended_metadata.appeal.structure_pattern && (
                          <CopyableField
                            label="構成パターン"
                            value={selectedProduct.extended_metadata.appeal.structure_pattern}
                            fieldName="structure_pattern"
                            copiedField={copiedField}
                            onCopy={copyToClipboard}
                          />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Competitors Section */}
                {selectedProduct.competitors && selectedProduct.competitors.length > 0 && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('competitors')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-700">競合情報</span>
                        <span className="text-xs text-gray-500">({selectedProduct.competitors.length}件)</span>
                      </div>
                      {expandedSections.competitors ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.competitors && (
                      <div className="p-4 space-y-4">
                        {selectedProduct.competitors.map((competitor, index) => (
                          <div key={competitor.id} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                競合{index + 1}: {competitor.name}
                              </span>
                              <button
                                onClick={() => copyToClipboard(formatCompetitorInfo(competitor), `competitor_${competitor.id}`)}
                                className="text-xs text-blue-600 hover:text-blue-700"
                              >
                                {copiedField === `competitor_${competitor.id}` ? 'コピー済み ✓' : 'コピー'}
                              </button>
                            </div>
                            <div className="space-y-2">
                              {competitor.company_url && (
                                <div className="text-xs">
                                  <span className="text-gray-500">URL: </span>
                                  <a href={competitor.company_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                    {competitor.company_url}
                                  </a>
                                </div>
                              )}
                              {competitor.description && (
                                <div className="text-xs">
                                  <span className="text-gray-500">概要: </span>
                                  <span className="text-gray-700">{competitor.description}</span>
                                </div>
                              )}
                              {competitor.strengths && (
                                <div className="text-xs">
                                  <span className="text-gray-500">強み: </span>
                                  <span className="text-gray-700">{competitor.strengths}</span>
                                </div>
                              )}
                              {competitor.weaknesses && (
                                <div className="text-xs">
                                  <span className="text-gray-500">弱み: </span>
                                  <span className="text-gray-700">{competitor.weaknesses}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Product Images Section */}
                {selectedProduct.product_images && selectedProduct.product_images.length > 0 && (
                  <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
                    <button
                      onClick={() => toggleSection('images')}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Image className="w-4 h-4 text-gray-500" />
                        <span className="font-medium text-gray-700">商品画像情報</span>
                        <span className="text-xs text-gray-500">({selectedProduct.product_images.length}件)</span>
                      </div>
                      {expandedSections.images ? (
                        <ChevronUp className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {expandedSections.images && (
                      <div className="p-4 space-y-4">
                        {selectedProduct.product_images.map((image, index) => (
                          <div key={image.id} className="border border-gray-100 rounded-lg p-3 bg-gray-50">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-medium text-gray-700">
                                画像{index + 1}: {getCategoryLabel(image.category)}
                              </span>
                              <button
                                onClick={() => copyToClipboard(formatImageInfo(image), `image_${image.id}`)}
                                className="text-xs text-blue-600 hover:text-blue-700"
                              >
                                {copiedField === `image_${image.id}` ? 'コピー済み ✓' : 'コピー'}
                              </button>
                            </div>
                            <div className="space-y-2">
                              {image.title && (
                                <div className="text-xs">
                                  <span className="text-gray-500">タイトル: </span>
                                  <span className="text-gray-700">{image.title}</span>
                                </div>
                              )}
                              {image.description && (
                                <div className="text-xs">
                                  <span className="text-gray-500">説明: </span>
                                  <span className="text-gray-700">{image.description}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Preview Section */}
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">プレビュー</span>
                    <button
                      onClick={() => copyToClipboard(formatProductInfo(selectedProduct), 'preview')}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      {copiedField === 'preview' ? 'コピー済み ✓' : 'コピー'}
                    </button>
                  </div>
                  <pre className="text-sm text-gray-600 whitespace-pre-wrap font-sans">
                    {formatProductInfo(selectedProduct)}
                  </pre>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-gray-500">
                <Package className="w-12 h-12 text-gray-300 mb-3" />
                <p>左側から商品を選択してください</p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};

// Copyable Field Component
interface CopyableFieldProps {
  label: string;
  value: string;
  fieldName: string;
  copiedField: string | null;
  onCopy: (text: string, fieldName: string) => void;
  multiline?: boolean;
}

const CopyableField: React.FC<CopyableFieldProps> = ({
  label,
  value,
  fieldName,
  copiedField,
  onCopy,
  multiline = false,
}) => {
  return (
    <div className="group">
      {label && <label className="text-xs font-medium text-gray-500 mb-1 block">{label}</label>}
      <div className="flex items-start gap-2">
        <div className={`flex-1 ${multiline ? 'text-sm text-gray-700 whitespace-pre-wrap' : 'text-sm text-gray-900'}`}>
          {value}
        </div>
        <button
          onClick={() => onCopy(value, fieldName)}
          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-100 rounded"
          title="コピー"
        >
          {copiedField === fieldName ? (
            <Check className="w-4 h-4 text-green-600" />
          ) : (
            <Copy className="w-4 h-4 text-gray-400" />
          )}
        </button>
      </div>
    </div>
  );
};

export default ProductInfoCopyModal;
